import React, { useEffect, useMemo, useState } from "react";
import { Row, Col, Form } from "react-bootstrap";
import Card from "../../../components/Card";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Dropdown } from "primereact/dropdown";
import { Dialog } from "primereact/dialog";
import Loader from "../../../components/partials/components/loader";
import { getApiCall } from "../../../service/getApiCall";
import { postApiCall } from "../../../service/postApiCall";
import { toast } from "react-toastify";
import {
  API_MANAGE_GROUP_LISTING, // /vX/groups
  API_USERS_LIST,           // /vX/users
  API_BULK_GROUP_DELETE,    // /vX/groups/bulk-delete
} from "../../../service/url";

export default function BulkGroupDelete() {
  const [loading, setLoading] = useState(false);

  // master lists
  const [groups, setGroups] = useState([]);     // [{group_id, group_name}]
  const [allUsers, setAllUsers] = useState([]); // full users list from API

  // working (filtered) list shown in table
  const [users, setUsers] = useState([]);

  // UI state
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [rows, setRows] = useState(10);
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [buttonDisable, setButtonDisable] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const [groupsRes, usersRes] = await Promise.all([
          getApiCall(API_MANAGE_GROUP_LISTING, {}),
          getApiCall(API_USERS_LIST, {}),
        ]);
        setGroups(Array.isArray(groupsRes) ? groupsRes : []);
        setAllUsers(Array.isArray(usersRes) ? usersRes : []);
        setUsers([]); // start empty until Filter is clicked (like your screenshot)
      } catch (err) {
        toast.error("Failed to load groups/users");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Filter button: show only members that belong to the selected group
  const applyGroupFilter = () => {
    if (!selectedGroupId) {
      toast.error("Please select a group first.");
      return;
    }

    console.log(selectedGroupId);
    const gid = String(selectedGroupId);

    const filtered = (allUsers || []).filter((u) =>
      Array.isArray(u.userGroups) &&
      u.userGroups.some((ug) => String(ug?.groups?.group_id) === gid)
    );

    console.log("new",filtered);
    setUsers(filtered);
    setSelectedUserIds([]);
  };

  console.log("FL", users);
  // Clear filter to empty list (as in your UI)
  const clearFilter = () => {
    setUsers([]);
    setSelectedUserIds([]);
    setSearchValue("");
  };

  // search within the filtered list
  const searched = useMemo(
    () =>
      users.filter((u) =>
        (u.username || "").toLowerCase().includes(searchValue.toLowerCase())
      ),
    [users, searchValue]
  );

  // Checkbox toggles
  const toggleUser = (userId) => {
    setSelectedUserIds((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  };

  // Select-all respects current search
  const toggleAll = () => {
    const visibleIds = searched.map((u) => u.id);
    const allSelected =
      visibleIds.length > 0 && visibleIds.every((id) => selectedUserIds.includes(id));

    if (allSelected) {
      setSelectedUserIds((prev) => prev.filter((id) => !visibleIds.includes(id)));
    } else {
      setSelectedUserIds((prev) => Array.from(new Set([...prev, ...visibleIds])));
    }
  };

  // Group names renderer (shows only names from the selected group, or all if none)
  const groupNamesBody = (row) => {
    if (!Array.isArray(row.userGroups)) return "-";
    const gid = selectedGroupId ? String(selectedGroupId) : null;
    const names = row.userGroups
      .filter((ug) => (gid ? String(ug?.groups?.group_id) === gid : true))
      .map((ug) => ug?.groups?.group_name)
      .filter(Boolean);
    return names.length ? names.join(", ") : "-";
  };

  const handleBulkDelete = async () => {
    if (!selectedGroupId) {
      toast.error("Please select a group first.");
      return;
    }
    if (selectedUserIds.length === 0) {
      toast.error("Select at least one user.");
      return;
    }

    try {
      setButtonDisable(true);
      const payload = { user_ids: selectedUserIds, group_id: selectedGroupId };
      const res = await postApiCall(API_BULK_GROUP_DELETE, payload);

      if (res) {
        toast.success(res.message || "Users removed from group successfully");
        // refresh by re-applying filter (so removed users disappear)
        applyGroupFilter();
        setSelectedUserIds([]);
      } else {
        toast.error(res?.message || "Failed to remove users");
      }
    } catch (err) {
      toast.error(err?.message || "Error removing users from group");
    } finally {
      setButtonDisable(false);
      setConfirmVisible(false);
    }
  };

  if (loading) return <Loader />;

  return (
    <div>
      <Row>
        <Col sm="12">
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h4 className="card-title">Bulk User Group Deletion</h4>
            </Card.Header>

            <Card.Body className="px-0">
              {/* Control row */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  padding: "10px",
                  flexWrap: "wrap",
                }}
              >
                {/* Show X items */}
                <span>Show</span>
                <Dropdown
                  value={rows}
                  options={[
                    { label: "5", value: 5 },
                    { label: "10", value: 10 },
                    { label: "20", value: 20 },
                    { label: "50", value: 50 },
                  ]}
                  onChange={(e) => setRows(e.value)}
                  style={{ width: 90, height: 40, display: "flex", alignItems: "center" }}
                />
                <span>items</span>

                {/* Search */}
                <Form.Control
                  style={{ width: 250, height: 40, marginLeft: 10 }}
                  placeholder="Search"
                  type="text"
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                />
                <Button label="Clear" className="p-button-secondary" onClick={clearFilter} />

                {/* Group selector + Filter */}
                <Dropdown
                  value={selectedGroupId}
                  options={groups.map((g) => ({ label: g.group_name, value: g.group_id }))}
                  onChange={(e) => setSelectedGroupId(e.value)}
                  placeholder="Select Group"
                  style={{ width: 260, marginLeft: 10 }}
                />
                <Button  label="Filter" className="p-button-secondary" onClick={applyGroupFilter} />

                {/* Delete */}
                <Button
                  label="Delete Selected"
                  className="p-button-danger"
                  disabled={selectedUserIds.length === 0 || !selectedGroupId || buttonDisable}
                  onClick={() => setConfirmVisible(true)}
                />
              </div>

              {/* Table */}
              <div className="scroll-container">
                <DataTable
                  value={searched}
                  paginator
                  rows={rows}
                  tableStyle={{ minWidth: "50rem" }}
                  emptyMessage="No data available"
                >
                  <Column
                    header={
                      <Checkbox
                        checked={
                          searched.length > 0 &&
                          searched.every((u) => selectedUserIds.includes(u.id))
                        }
                        onChange={toggleAll}
                      />
                    }
                    headerStyle={{ width: "3rem" }}
                    body={(row) => (
                      <Checkbox
                        checked={selectedUserIds.includes(row.id)}
                        onChange={() => toggleUser(row.id)}
                      />
                    )}
                  />
                  <Column field="id" header="User Id" sortable />
                  <Column field="username" header="User Name" sortable />
                  <Column header="Group Name" body={groupNamesBody} sortable />
                </DataTable>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Confirm Modal */}
      <Dialog
        header="Confirm Bulk Delete"
        visible={confirmVisible}
        style={{ width: "25vw" }}
        onHide={() => setConfirmVisible(false)}
        footer={
          <div>
            <Button label="Cancel" className="p-button-text" onClick={() => setConfirmVisible(false)} />
            <Button
              label="Confirm"
              className="p-button-danger"
              onClick={handleBulkDelete}
              disabled={buttonDisable}
            />
          </div>
        }
      >
        <p>Are you sure you want to remove {selectedUserIds.length} user(s) from this group?</p>
      </Dialog>
    </div>
  );
}
